import React, { useState, useEffect, useCallback } from "react";
import { useMutation, gql } from "@apollo/client";
import {
  Grid,
  Typography,
  Box,
  TextField,
  Switch,
  Alert,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ReactQuill from "react-quill";
import { MuiColorInput } from "mui-color-input";
import {
  TextFields as TextFieldsIcon,
  Palette as PaletteIcon,
  Message as MessageIcon,
  Tune as TuneIcon,
  Chat as ChatIcon,
  Videocam as VideoIcon,
  Call as CallIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

// Styled Components - Matching DisplayOptions.js pattern
const ConfigPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  height: "100%",
  backgroundColor: theme.palette.background.paper,
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const SettingsToggle = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: 8,
  marginBottom: theme.spacing(1),
  "&:last-child": {
    marginBottom: 0,
  },
}));

const ColorPickerRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ColorSwatch = styled(Box)(({ color }) => ({
  width: 40,
  height: 40,
  borderRadius: 8,
  backgroundColor: color,
  border: "2px solid rgba(0,0,0,0.1)",
  flexShrink: 0,
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
}));

const QuillContainer = styled(Box)(({ theme }) => ({
  "& .ql-container": {
    borderRadius: "0 0 8px 8px",
    minHeight: 120,
    fontSize: 14,
  },
  "& .ql-toolbar": {
    borderRadius: "8px 8px 0 0",
    backgroundColor: theme.palette.grey[50],
  },
  "& .ql-editor": {
    minHeight: 100,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
  },
}));

// Preview Components
const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: "#f5f7fa",
  minHeight: 600,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const PreviewSection = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  borderRadius: 8,
  padding: theme.spacing(2),
  boxShadow: theme.shadows[1],
}));

const PreviewLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

// Widget Preview Elements
const FloatingButtonPreview = styled(Box)(({ buttonColor }) => ({
  width: 56,
  height: 56,
  borderRadius: "50%",
  backgroundColor: buttonColor || "#4CAF50",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
  color: "white",
}));

const SpeechBubblePreview = styled(Box)({
  backgroundColor: "white",
  padding: "12px 16px",
  borderRadius: 8,
  boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
  maxWidth: 220,
  fontSize: 13,
  lineHeight: 1.4,
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -8,
    right: 20,
    borderLeft: "8px solid transparent",
    borderRight: "8px solid transparent",
    borderTop: "8px solid white",
  },
});

const ChatWindowPreview = styled(Box)({
  maxWidth: 320,
  backgroundColor: "#ffffff",
  borderRadius: 12,
  boxShadow: "0 5px 40px rgba(0,0,0,0.2)",
  overflow: "hidden",
  margin: "0 auto",
});

const ChatHeaderPreview = styled(Box)(
  ({ buttonColor, fontColor, fontShadow }) => ({
    backgroundColor: buttonColor || "#4CAF50",
    color: fontColor || "#ffffff",
    padding: "16px",
    borderRadius: "12px 12px 0 0",
    display: "flex",
    alignItems: "center",
    gap: 12,
    position: "relative",
    textShadow: fontShadow ? "1px 1px 2px rgba(0,0,0,0.3)" : "none",
  })
);

const ChannelButtonPreview = styled(Box)(({ theme }) => ({
  padding: "16px 12px",
  border: "2px solid #e0e0e0",
  borderRadius: 8,
  backgroundColor: "#fff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: "#1976d2",
    transform: "translateY(-2px)",
  },
}));

const ChannelIcon = styled(Box)(({ bgColor }) => ({
  width: 40,
  height: 40,
  borderRadius: "50%",
  backgroundColor: bgColor || "#1976d2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
}));

const UPDATE_WIDGET = gql`
  mutation UpdateWidget($id: ID!, $input: WidgetInput!) {
    updateWidget(id: $id, input: $input) {
      id
      website_integration {
        speeach_bubble_test
        call_to_action_button_text
        slogan
      }
      welcome_screen {
        font_shadow
        customizable_button
        button_color
        font_color
        icon_color
        welcome_message
      }
    }
  }
`;

const Text_Colors = ({
  handleChildData,
  widgetData,
  isEditing,
  widgetId,
  onSave,
}) => {
  // Initialize state with widget data or defaults
  const [buttonText, setButtonText] = useState(
    widgetData?.website_integration?.call_to_action_button_text || "Yes gladly"
  );
  const [slogan, setSlogan] = useState(
    widgetData?.website_integration?.slogan || "Let's Conn777"
  );
  const [isCustomizableButton, setIsCustomizableButton] = useState(
    widgetData?.welcome_screen?.customizable_button ?? false
  );
  const [customButtonText, setCustomButtonText] = useState("More information");
  const [customButtonLink, setCustomButtonLink] = useState("");
  const [buttonColor, setButtonColor] = useState(
    widgetData?.welcome_screen?.button_color || "#4CAF50"
  );
  const [fontColor, setFontColor] = useState(
    widgetData?.welcome_screen?.font_color || "#ffffff"
  );
  const [iconColor, setIconColor] = useState(
    widgetData?.welcome_screen?.icon_color || "#000000"
  );
  const [speechBubbleText, setSpeechBubbleText] = useState(
    widgetData?.website_integration?.speeach_bubble_test ||
      "May I help you further? I would be happy to support you personally and immediately."
  );
  const [welcomeMessage, setWelcomeMessage] = useState(
    widgetData?.welcome_screen?.welcome_message ||
      `Great to have you here!

    You are interested in one of our products? Just let us know how we can help you and you will be connected directly with one of our consultants.

    Thank you for your patience!`
  );
  const [activateFontShadow, setActivateFontShadow] = useState(
    widgetData?.welcome_screen?.font_shadow ?? true
  );

  // Update state when widget data changes (for edit mode)
  useEffect(() => {
    if (widgetData) {
      const { website_integration, welcome_screen } = widgetData;

      if (website_integration) {
        setButtonText(
          website_integration.call_to_action_button_text || "Yes gladly"
        );
        setSlogan(website_integration.slogan || "Let's Conn777");
        setSpeechBubbleText(
          website_integration.speeach_bubble_test ||
            "May I help you further? I would be happy to support you personally and immediately."
        );
      }

      if (welcome_screen) {
        setIsCustomizableButton(welcome_screen.customizable_button ?? false);
        setButtonColor(welcome_screen.button_color || "#4CAF50");
        setFontColor(welcome_screen.font_color || "#ffffff");
        setIconColor(welcome_screen.icon_color || "#000000");
        setWelcomeMessage(
          welcome_screen.welcome_message ||
            `Great to have you here!

    You are interested in one of our products? Just let us know how we can help you and you will be connected directly with one of our consultants.

    Thank you for your patience!`
        );
        setActivateFontShadow(welcome_screen.font_shadow ?? true);
      }
    }
  }, [widgetData]);

  const [updateWidget] = useMutation(UPDATE_WIDGET, {
    onError: (error) => {
      console.error("Error updating widget text colors:", error);
    },
  });

  const handleSaveTextColors = useCallback(async () => {
    if (!widgetId) return;

    try {
      const widgetInput = {
        business_details: widgetData?.business_details
          ? {
              business_type: widgetData.business_details.business_type,
              name: widgetData.business_details.name,
              display_name: widgetData.business_details.display_name,
              website_url: widgetData.business_details.website_url,
            }
          : undefined,
        website_integration: {
          // Keep existing display options
          mode: widgetData?.website_integration?.mode,
          button_style: widgetData?.website_integration?.button_style,
          position: widgetData?.website_integration?.position,
          square_buttom: widgetData?.website_integration?.square_buttom,
          minimize_small_button:
            widgetData?.website_integration?.minimize_small_button,
          preview: widgetData?.website_integration?.preview,
          media: widgetData?.website_integration?.media,
          picture: widgetData?.website_integration?.picture,
          // Update text-related fields
          speeach_bubble_test: speechBubbleText,
          call_to_action_button_text: buttonText,
          slogan: slogan,
        },
        welcome_screen: {
          font_shadow: activateFontShadow,
          desc: widgetData?.welcome_screen?.desc,
          customizable_button: isCustomizableButton,
          button_color: buttonColor,
          font_color: fontColor,
          icon_color: iconColor,
          welcome_message: welcomeMessage,
        },
      };

      const { data } = await updateWidget({
        variables: {
          id: widgetId,
          input: widgetInput,
        },
      });

      if (data && data.updateWidget) {
        console.log("Text colors updated successfully:", data.updateWidget);
      } else {
        throw new Error("Failed to update text colors");
      }
    } catch (err) {
      console.error("Error saving text colors:", err);
      throw err;
    }
  }, [
    widgetId,
    speechBubbleText,
    buttonText,
    slogan,
    activateFontShadow,
    isCustomizableButton,
    buttonColor,
    fontColor,
    iconColor,
    welcomeMessage,
    widgetData,
    updateWidget,
  ]);

  // Expose save function to parent component
  useEffect(() => {
    if (onSave && typeof onSave === "function") {
      onSave(handleSaveTextColors);
    }
  }, [onSave, handleSaveTextColors]);

  const handleCustomizableButtonChange = (event) => {
    setIsCustomizableButton(event.target.checked);
  };

  return (
    <Grid container spacing={4}>
      {/* Header Section */}
      <Grid item xs={12}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Customize Your Widget
        </Typography>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Please create all your texts in English or leave the default texts.
          You can manage the translations in Translations. We always show the
          widget in the browser language the user has chosen. If this is not
          available, English will be default.
        </Alert>
      </Grid>

      {/* Configuration Panel */}
      <Grid item xs={12} md={5}>
        <ConfigPanel elevation={3}>
          {/* Section 1: Text Content */}
          <Section>
            <SectionTitle variant="h6">
              <TextFieldsIcon color="primary" />
              Text Content
            </SectionTitle>

            <Box mb={2}>
              <StyledTextField
                fullWidth
                label="Speech Bubble Text"
                multiline
                rows={2}
                value={speechBubbleText}
                onChange={(e) => setSpeechBubbleText(e.target.value)}
                helperText="Text shown above the floating button"
                size="small"
              />
            </Box>

            <Box mb={2}>
              <StyledTextField
                fullWidth
                label="Call To Action Button"
                value={buttonText}
                onChange={(e) => setButtonText(e.target.value)}
                helperText="Button text for starting conversation"
                size="small"
              />
            </Box>

            <Box>
              <StyledTextField
                fullWidth
                label="Header Slogan"
                value={slogan}
                onChange={(e) => setSlogan(e.target.value)}
                helperText="Subtitle shown in the chat header"
                size="small"
              />
            </Box>
          </Section>

          {/* Section 2: Welcome Message */}
          <Section>
            <SectionTitle variant="h6">
              <MessageIcon color="primary" />
              Welcome Message
            </SectionTitle>
            <QuillContainer>
              <ReactQuill
                theme="snow"
                value={welcomeMessage}
                onChange={setWelcomeMessage}
                placeholder="Enter your welcome message..."
              />
            </QuillContainer>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              This message is shown when visitors open the chat widget
            </Typography>
          </Section>

          {/* Section 3: Colors */}
          <Section>
            <SectionTitle variant="h6">
              <PaletteIcon color="primary" />
              Widget Colors
            </SectionTitle>

            <ColorPickerRow>
              <ColorSwatch color={buttonColor} />
              <MuiColorInput
                format="hex"
                value={buttonColor}
                onChange={setButtonColor}
                label="Primary Color"
                size="small"
                sx={{ flex: 1 }}
              />
            </ColorPickerRow>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block", ml: 7 }}
            >
              Used for header background and accent elements
            </Typography>

            <ColorPickerRow>
              <ColorSwatch color={fontColor} />
              <MuiColorInput
                format="hex"
                value={fontColor}
                onChange={setFontColor}
                label="Header Text Color"
                size="small"
                sx={{ flex: 1 }}
              />
            </ColorPickerRow>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block", ml: 7 }}
            >
              Text color in the chat header
            </Typography>

            <ColorPickerRow>
              <ColorSwatch color={iconColor} />
              <MuiColorInput
                format="hex"
                value={iconColor}
                onChange={setIconColor}
                label="Icon Color"
                size="small"
                sx={{ flex: 1 }}
              />
            </ColorPickerRow>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", ml: 7 }}
            >
              Color for widget icons
            </Typography>
          </Section>

          {/* Section 4: Display Options */}
          <Section>
            <SectionTitle variant="h6">
              <TuneIcon color="primary" />
              Display Options
            </SectionTitle>

            <SettingsToggle>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Font Shadow
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add subtle shadow to header text
                </Typography>
              </Box>
              <Switch
                checked={activateFontShadow}
                onChange={(e) => setActivateFontShadow(e.target.checked)}
                color="primary"
              />
            </SettingsToggle>

            <SettingsToggle>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Custom Button
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add a custom CTA button to welcome screen
                </Typography>
              </Box>
              <Switch
                checked={isCustomizableButton}
                onChange={handleCustomizableButtonChange}
                color="primary"
              />
            </SettingsToggle>

            {isCustomizableButton && (
              <Box
                sx={{
                  mt: 2,
                  pl: 2,
                  borderLeft: "3px solid",
                  borderColor: "primary.main",
                }}
              >
                <Box mb={2}>
                  <StyledTextField
                    fullWidth
                    label="Button Text"
                    value={customButtonText}
                    onChange={(e) => setCustomButtonText(e.target.value)}
                    size="small"
                  />
                </Box>
                <Box>
                  <StyledTextField
                    fullWidth
                    label="Button Link"
                    value={customButtonLink}
                    onChange={(e) => setCustomButtonLink(e.target.value)}
                    placeholder="https://example.com"
                    size="small"
                  />
                </Box>
              </Box>
            )}
          </Section>
        </ConfigPanel>
      </Grid>

      {/* Preview Panel */}
      <Grid item xs={12} md={7}>
        <PreviewContainer elevation={3}>
          <SectionTitle variant="h6" sx={{ mb: 1 }}>
            <ChatIcon color="primary" />
            Live Preview
          </SectionTitle>

          {/* Floating Button Preview */}
          <PreviewSection>
            <PreviewLabel>Floating Button</PreviewLabel>
            <Box
              sx={{
                backgroundColor: "#e8eef4",
                borderRadius: 2,
                p: 3,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-end",
                gap: 2,
                minHeight: 120,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 1,
                }}
              >
                <SpeechBubblePreview>
                  {speechBubbleText || "Your speech bubble text here..."}
                </SpeechBubblePreview>
                <FloatingButtonPreview buttonColor={buttonColor}>
                  <ChatIcon />
                </FloatingButtonPreview>
              </Box>
            </Box>
          </PreviewSection>

          {/* Chat Window Preview */}
          <PreviewSection sx={{ flex: 1 }}>
            <PreviewLabel>Chat Window</PreviewLabel>
            <ChatWindowPreview>
              {/* Chat Header */}
              <ChatHeaderPreview
                buttonColor={buttonColor}
                fontColor={fontColor}
                fontShadow={activateFontShadow}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ChatIcon sx={{ color: fontColor }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ color: fontColor }}
                  >
                    {widgetData?.business_details?.display_name ||
                      "Your Business"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: fontColor, opacity: 0.9 }}
                  >
                    {slogan || "Your slogan here"}
                  </Typography>
                </Box>
                <CloseIcon sx={{ color: fontColor, opacity: 0.7 }} />
              </ChatHeaderPreview>

              {/* Chat Body */}
              <Box sx={{ p: 2, backgroundColor: "#f9fafb", minHeight: 200 }}>
                {/* Welcome Message */}
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    <span
                      dangerouslySetInnerHTML={{ __html: welcomeMessage }}
                    />
                  </Typography>
                </Box>

                {/* Channel Buttons */}
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <ChannelButtonPreview>
                      <ChannelIcon bgColor={buttonColor}>
                        <ChatIcon fontSize="small" />
                      </ChannelIcon>
                      <span>Chat</span>
                    </ChannelButtonPreview>
                  </Grid>
                  <Grid item xs={6}>
                    <ChannelButtonPreview>
                      <ChannelIcon bgColor="#FF5722">
                        <VideoIcon fontSize="small" />
                      </ChannelIcon>
                      <span>Video</span>
                    </ChannelButtonPreview>
                  </Grid>
                  <Grid item xs={6}>
                    <ChannelButtonPreview>
                      <ChannelIcon bgColor="#9C27B0">
                        <CallIcon fontSize="small" />
                      </ChannelIcon>
                      <span>Audio</span>
                    </ChannelButtonPreview>
                  </Grid>
                  <Grid item xs={6}>
                    <ChannelButtonPreview>
                      <ChannelIcon bgColor="#2196F3">
                        <PhoneIcon fontSize="small" />
                      </ChannelIcon>
                      <span>Phone</span>
                    </ChannelButtonPreview>
                  </Grid>
                </Grid>

                {/* Custom Button Preview */}
                {isCustomizableButton && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: buttonColor,
                      color: fontColor,
                      borderRadius: 1,
                      textAlign: "center",
                      fontWeight: 500,
                      fontSize: 14,
                      cursor: "pointer",
                      "&:hover": {
                        opacity: 0.9,
                      },
                    }}
                  >
                    {customButtonText || "More information"}
                  </Box>
                )}
              </Box>
            </ChatWindowPreview>
          </PreviewSection>
        </PreviewContainer>
      </Grid>
    </Grid>
  );
};

export default Text_Colors;
export { UPDATE_WIDGET };
